import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BatteryMedium, ShoppingBag, Check, ShieldCheck } from "lucide-react";
import { formatPrice, productMessage, waLink, categoryOf, accTypeOf, usesModels, ACC_TYPES, isNewCond, warrantyOf, appleUntil } from "@/lib/store";
import { installmentText, promoOf } from "@/lib/pricing";
import { PriceBlock } from "./PriceBlock";
import { useCart, useInterestClick, isAccessory } from "@/lib/cart";
import { PhotoGallery } from "./PhotoGallery";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { AppleMark, isAllOriginal } from "./PartsInfo";
import { EASE } from "./Reveal";
import { FavoriteButton } from "./FavoriteButton";
import { HotBadge } from "./HotBadge";

const shortCondition = (p) => (categoryOf(p) === "acessorios" ? "HPrime" : p.condition === "anatel" ? "Lacrado • Anatel" : p.condition === "novo" ? "Novo • Lacrado" : "Seminovo");

const subtitle = (p) => (isAccessory(p)
  ? [ACC_TYPES.find((t) => t.id === accTypeOf(p))?.single, accTypeOf(p) === "fonte" ? p.wattage : null].filter(Boolean).join(" · ")
  : [p.storage, p.color].filter(Boolean).join(" · "));

// Preenche o espaço dos cards sem oferta (que têm menos linhas de preço) com garantia e originalidade.
// No celular o card é estreito: "1 ano de garantia Apple" vira "Garantia de 1 ano" (o "Original Apple" vem logo abaixo).
const shortWarranty = (p) => {
  if (appleUntil(p)) return `Apple até ${appleUntil(p).slice(3)}`;
  const m = warrantyOf(p).match(/^(\d+ \S+) de garantia/);
  return m ? `Garantia de ${m[1]}` : warrantyOf(p);
};
const TrustLines = ({ p }) => (
  <ul data-testid={`product-trust-${p.id}`} className="mt-2 space-y-1 text-[12px] leading-tight text-zinc-300">
    <li className="flex items-center gap-1.5">
      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-300" />
      <span data-testid={`product-warranty-${p.id}`} className="truncate">
        <span className="sm:hidden">{shortWarranty(p)}</span>
        <span className="hidden sm:inline">{warrantyOf(p).replace(" contra defeitos de fábrica", "")}</span>
      </span>
    </li>
    {isAllOriginal(p) && (
      <li data-testid={`product-original-line-${p.id}`} className="flex items-center gap-1.5">
        <AppleMark className="h-3.5 w-3.5 shrink-0 text-white" /> <span className="truncate">Original Apple</span>
      </li>
    )}
  </ul>
);

const CompactActions = ({ p, preferredModel }) => {
  const navigate = useNavigate();
  const { add, has, setOpen } = useCart();
  const interest = useInterestClick(p);
  const compat = Array.isArray(p.compatible) ? p.compatible : [];
  const picks = isAccessory(p) && usesModels(accTypeOf(p));
  const forModel = picks ? (compat.length === 1 ? compat[0] : compat.includes(preferredModel) ? preferredModel : "") : "";
  const needsPick = picks && compat.length > 1 && !forModel;
  const inCart = has(p, forModel);
  const toPage = (e) => { e.preventDefault(); navigate(`/produto/${p.id}`); };
  const onCart = (e) => {
    if (needsPick) return toPage(e);
    if (inCart && !isAccessory(p)) return setOpen(true);
    return add(p, forModel);
  };
  return (
    <div className="mt-3 flex gap-2">
      <a data-testid={`product-interest-btn-${p.id}`} data-pid={p.id} data-pmodel={p.model} href={waLink(productMessage(p, forModel))} target="_blank" rel="noopener noreferrer"
        onClick={needsPick ? toPage : interest} aria-label={`Tenho interesse no ${p.model}`}
        className="flex flex-1 items-center justify-center gap-1 rounded-full bg-[#25D366] font-bold text-black h-9 text-[12px] active:scale-95">
        <WhatsAppIcon className="h-3.5 w-3.5" /> Quero
      </a>
      <button data-testid={`add-to-cart-${p.id}`} onClick={onCart} aria-label={inCart ? "No carrinho" : "Adicionar ao carrinho"}
        className={`flex shrink-0 items-center justify-center h-9 w-9 rounded-full border active:scale-95 ${inCart ? "border-emerald-400/40 text-emerald-300" : "border-white/20 text-white"}`}>
        {inCart ? <Check className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
      </button>
    </div>
  );
};

export const CompactProductCard = ({ p, index, preferredModel }) => (
  <motion.article data-testid={`product-card-${p.id}`} layout
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, delay: (index % 2) * 0.06, ease: EASE }}
    className="flex flex-col overflow-hidden rounded-[20px] border border-white/10 bg-[#111113]">
    <div className="relative aspect-[4/5] overflow-hidden bg-zinc-950">
      <div className="absolute inset-0">
        <PhotoGallery images={p.images} alt={p.model} id={p.id} to={`/produto/${p.id}`} dotsClass="bottom-2 left-1/2 -translate-x-1/2" />
      </div>
      <FavoriteButton p={p} size="sm" className="absolute right-2 top-2" />
      <div className="pointer-events-none absolute left-2 top-2 flex flex-col items-start gap-1">
        <span data-testid={`product-condition-${p.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] ${isNewCond(p.condition) && !isAccessory(p) ? "bg-white text-black" : "border border-white/20 bg-black/70 text-white backdrop-blur"}`}>
          {shortCondition(p)}
        </span>
        <HotBadge id={p.id} size="sm" />
        {!isNewCond(p.condition) && !isAccessory(p) && appleUntil(p) && (
          <span data-testid={`product-apple-warranty-badge-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-black backdrop-blur">
            <AppleMark className="h-2.5 w-2.5" /> Garantia Apple
          </span>
        )}
      </div>
      {p.battery != null && (
        <span className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[11px] text-emerald-300 backdrop-blur">
          <BatteryMedium className="h-3 w-3" /> {p.battery}%
        </span>
      )}
      {isAllOriginal(p) && (
        <span data-testid={`product-original-seal-${p.id}`} title="Original Apple" className="pointer-events-none absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white backdrop-blur">
          <AppleMark className="h-3 w-3" />
        </span>
      )}
    </div>
    <div className="flex flex-1 flex-col p-3">
      <Link to={`/produto/${p.id}`} data-testid={`product-link-${p.id}`} className="flex flex-1 flex-col">
        <p className="mono truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">{subtitle(p) || "\u00a0"}</p>
        <h3 data-testid={`product-model-${p.id}`} className="line-clamp-2 font-semibold leading-snug text-white mt-1 text-[15px]">{p.model}</h3>
        {!promoOf(p) && <TrustLines p={p} />}
        <div className="mt-auto pt-2">
          {promoOf(p) ? (
            <PriceBlock p={p} size="compact" priceTestid={`product-price-${p.id}`} installmentTestid={`product-installment-${p.id}`} />
          ) : (
            <>
              {installmentText(p) && <p data-testid={`product-installment-${p.id}`} className="font-bold leading-tight text-emerald-300 text-[13px]">{installmentText(p)}</p>}
              <p data-testid={`product-price-${p.id}`} className={installmentText(p) ? "mt-0.5 text-xs text-zinc-300" : "font-display font-bold text-white text-lg"}>
                {installmentText(p) ? `À vista: ${formatPrice(p.price)}` : formatPrice(p.price)}
              </p>
            </>
          )}
        </div>
      </Link>
      <CompactActions p={p} preferredModel={preferredModel} />
    </div>
  </motion.article>
);
